// Library Methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import { SelectChangeEvent } from "@mui/material/Select";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";

// Config
import { allLanguageMap, priorityLanguageMap } from "src/config/languages";

// Types
import { UpdateClinicSettingsType } from "src/services/clinics/types";

type PatientLanguageDropdownType = {
  isDisabled: boolean;
  currentSettings: UpdateClinicSettingsType;
  handleUpdateSettings: (settings: UpdateClinicSettingsType) => void;
};

const PatientLanguageDropdown = ({
  isDisabled,
  currentSettings,
  handleUpdateSettings,
}: PatientLanguageDropdownType) => {
  const { t } = useTranslation();

  // sorted priority languages
  const clinicDropdownOptions = useMemo(
    () =>
      Object.entries(priorityLanguageMap)
        .map(([_, lang]) => ({ displayName: t(lang.label), value: lang.label }))
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    [t]
  );

  // all sorted languages, with priority languages at the beginning of the array
  const patientDropdownOptions = useMemo(() => {
    let patientOptions = Object.entries(allLanguageMap)
      .filter(
        ([_, lang]) =>
          !clinicDropdownOptions.find((option) => option.value === lang.value)
      )
      .map(([_, lang]) => ({ displayName: t(lang.label), value: lang.label }))
      .sort((a, b) => a?.displayName?.localeCompare(b?.displayName));

    patientOptions.unshift(...clinicDropdownOptions);
    return patientOptions;
  }, [t, clinicDropdownOptions]);

  const handleSetPatientLanguage = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = patientDropdownOptions.find(
        (option) => option.displayName === event.target.value
      )?.value;

      if (value) {
        handleUpdateSettings({
          ...currentSettings,
          patientDefaultLanguage: value,
        });
      }
    },
    [patientDropdownOptions, currentSettings, handleUpdateSettings]
  );

  return (
    <SelectDropdown
      id="patient-language-dropdown"
      disabled={isDisabled}
      dropdownOptions={patientDropdownOptions}
      value={t(currentSettings.patientDefaultLanguage)}
      handleChange={handleSetPatientLanguage}
    />
  );
};

export default PatientLanguageDropdown;
